import { useEffect, lazy, Suspense } from "react";
import { NotebookPen, Languages, Sparkles } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ScrollTrigger } from "@/lib/gsap";
import { StoryHero } from "./story/StoryHero";

/* StoryHero is the LCP element, so it stays eager. Everything below the fold is
 * lazy-loaded: React can then commit and paint the hero without first rendering
 * (and running GSAP SplitText setup for) all ten sections, which is what pushed
 * mobile LCP/TBT out. The below-fold chunks stream in right after first paint —
 * off-screen, so no visible pop and CLS stays 0. */
const StoryControl = lazy(() => import("./story/StoryControl").then((m) => ({ default: m.StoryControl })));
const StoryBotFree = lazy(() => import("./story/StoryBotFree").then((m) => ({ default: m.StoryBotFree })));
const StoryManifesto = lazy(() => import("./story/StoryManifesto").then((m) => ({ default: m.StoryManifesto })));
const StoryPillarSection = lazy(() => import("./story/StoryPillarSection").then((m) => ({ default: m.StoryPillarSection })));
const StoryPricing = lazy(() => import("./story/StoryPricing").then((m) => ({ default: m.StoryPricing })));
const StoryProof = lazy(() => import("./story/StoryProof").then((m) => ({ default: m.StoryProof })));
const StoryCta = lazy(() => import("./story/StoryCta").then((m) => ({ default: m.StoryCta })));

/* GSAP scroll-storytelling landing page, served at / */
export default function Story() {
  useEffect(() => {
    // Re-measure trigger positions once fonts, images, and lazy sections settle
    const refresh = () => ScrollTrigger.refresh();
    document.fonts?.ready.then(refresh);
    window.addEventListener("load", refresh);
    return () => window.removeEventListener("load", refresh);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <main>
        <StoryHero />
        <Suspense fallback={null}>
          <StoryControl />
          <StoryBotFree />
          <StoryManifesto />
          <StoryPillarSection
            pillarKey="notes"
            icon={NotebookPen}
            accent="emerald"
            href="/live-notes"
            pointCount={4}
            images={[
              "/images/feature-notes.webp",
              "/images/notes-live.webp",
              "/images/notes-group.webp",
              "/images/notes-media.webp",
            ]}
          />
          <StoryPillarSection
            pillarKey="translator"
            icon={Languages}
            accent="cyan"
            href="/live-translator"
            pointCount={4}
            reverse
            bgSoft
            images={[
              "/images/translator-voice.webp",
              "/images/translator-screen.webp",
              "/images/translator-camera.webp",
              "/images/feature-translator.webp",
            ]}
          />
          <StoryPillarSection
            pillarKey="agent"
            icon={Sparkles}
            accent="purple"
            href="/live-agent"
            pointCount={3}
            comingSoon
            images={[
              "/images/agent-hero.webp",
              "/images/agent-sales.webp",
              "/images/agent-interviews.webp",
            ]}
          />
          <StoryPricing />
          <StoryProof />
          <StoryCta />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
